import { LitElement, html, css, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { windowsSelectFix } from './dashboard-styles';

interface AppUser {
  id: number;
  email: string;
  phone_e164: string;
  role: string;
  active: boolean;
}

@customElement('fp3-users')
export class Fp3Users extends LitElement {
  @state() private users: AppUser[] = [];
  @state() private error = '';
  @state() private email = '';
  @state() private phone = '';
  @state() private password = '';
  @state() private newRole = 'user';
  @state() private drafts: Record<number, { phone: string; role: string; password: string; active: boolean }> = {};

  static styles = [css`
    :host { display: block; }
    h2 { font-size: 1.2rem; font-weight: 600; margin: 0 0 0.3rem; letter-spacing: -0.02em; }
    .subtitle { color: var(--dash-muted, #8fa3b8); font-size: 0.9rem; line-height: 1.45; margin: 0 0 1rem; }
    form, .roster {
      display: grid;
      gap: 0.75rem;
    }
    .add, .person {
      padding: 1rem;
      border: 1px solid var(--dash-border, #314255);
      border-radius: 12px;
      background: var(--dash-surface, #121a22);
    }
    .add { margin-bottom: 0.4rem; }
    .add-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(170px, 1fr)); gap: 0.7rem; align-items: end; }
    .section-label {
      margin: 0 0 0.75rem;
      font-size: 0.72rem;
      font-weight: 700;
      letter-spacing: 0.06em;
      text-transform: uppercase;
      color: var(--dash-faint, #6d8299);
    }
    label {
      display: grid;
      gap: 0.3rem;
      font-size: 0.68rem;
      font-weight: 600;
      letter-spacing: 0.06em;
      text-transform: uppercase;
      color: var(--dash-muted, #8fa3b8);
    }
    input, select, button {
      font: inherit;
      color: var(--dash-text, #e8edf2);
      background: var(--dash-input-bg, #0f1419);
      border: 1px solid var(--dash-border, #314255);
      border-radius: 8px;
      padding: 0.5rem 0.65rem;
    }
    input:focus, select:focus { outline: 2px solid var(--dash-accent, #5b9fd4); outline-offset: 1px; }
    button { cursor: pointer; }
    button.primary { background: var(--dash-accent, #5b9fd4); color: #0f1419; border-color: var(--dash-accent, #5b9fd4); font-weight: 600; }
    .person { display: grid; gap: 0.75rem; }
    .person-head { display: flex; justify-content: space-between; align-items: center; gap: 0.75rem; }
    .email { font-weight: 600; }
    .role {
      font-size: 0.68rem;
      font-weight: 700;
      letter-spacing: 0.05em;
      text-transform: uppercase;
      padding: 0.2rem 0.45rem;
      border-radius: 999px;
      background: var(--dash-accent-soft, rgba(91, 159, 212, 0.16));
      color: var(--dash-accent-2, #b7d7ee);
    }
    .fields {
      display: grid;
      grid-template-columns: minmax(140px, 1.1fr) 130px auto minmax(150px, 1fr) auto;
      gap: 0.65rem;
      align-items: end;
    }
    .error { color: #f0a8a8; margin: 0 0 0.8rem; }
    .check { align-self: center; text-transform: none; letter-spacing: normal; font-size: 0.85rem; font-weight: 500; color: var(--dash-text, #e8edf2); display: flex; gap: 0.4rem; align-items: center; }
    .empty {
      margin: 0;
      padding: 1rem;
      border: 1px dashed var(--dash-border, #314255);
      border-radius: 12px;
      color: var(--dash-muted, #8fa3b8);
    }
    @media (max-width: 900px) { .fields { grid-template-columns: 1fr 1fr; } }
  `, windowsSelectFix];

  connectedCallback() {
    super.connectedCallback();
    this.reload();
  }

  private async reload() {
    this.error = '';
    const res = await fetch('/api/admin/users');
    if (!res.ok) {
      this.error = 'The account list is not available.';
      return;
    }
    const body = await res.json();
    this.users = body.users;
    const drafts = { ...this.drafts };
    for (const user of this.users) {
      if (!drafts[user.id]) {
        drafts[user.id] = { phone: user.phone_e164, role: user.role, password: '', active: user.active };
      } else {
        drafts[user.id] = { ...drafts[user.id], phone: user.phone_e164, role: user.role, active: user.active, password: '' };
      }
    }
    this.drafts = drafts;
  }

  private async addUser(event: Event) {
    event.preventDefault();
    this.error = '';
    const res = await fetch('/api/admin/users', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: this.email,
        phone: this.phone,
        password: this.password,
        role: this.newRole,
      }),
    });
    if (!res.ok) {
      this.error = await readError(res);
      return;
    }
    this.email = '';
    this.phone = '';
    this.password = '';
    this.newRole = 'user';
    await this.reload();
  }

  private draft(user: AppUser) {
    return this.drafts[user.id] || { phone: user.phone_e164, role: user.role, password: '', active: user.active };
  }

  private setDraft(id: number, patch: Partial<{ phone: string; role: string; password: string; active: boolean }>) {
    const current = this.drafts[id];
    this.drafts = { ...this.drafts, [id]: { ...current, ...patch } };
  }

  private async save(user: AppUser) {
    this.error = '';
    const draft = this.draft(user);
    const body: Record<string, unknown> = {
      phone: draft.phone,
      role: draft.role,
      active: draft.active,
    };
    if (draft.password) body.password = draft.password;
    const res = await fetch(`/api/admin/users/${user.id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      this.error = await readError(res);
      return;
    }
    await this.reload();
  }

  render() {
    return html`
      <h2>People</h2>
      <p class="subtitle">Add the email, mobile number, and a starting password. They sign in with that password, then the code texted to the number.</p>
      ${this.error ? html`<p class="error">${this.error}</p>` : nothing}
      <form class="add" @submit=${this.addUser}>
        <p class="section-label">Add a person</p>
        <div class="add-grid">
        <label>Email
          <input type="email" required .value=${this.email} @input=${(event: Event) => { this.email = (event.target as HTMLInputElement).value; }} />
        </label>
        <label>Mobile number
          <input required placeholder="+1 202 555 0142" .value=${this.phone} @input=${(event: Event) => { this.phone = (event.target as HTMLInputElement).value; }} />
        </label>
        <label>Starting password
          <input type="text" required minlength="10" autocomplete="new-password" .value=${this.password}
            @input=${(event: Event) => { this.password = (event.target as HTMLInputElement).value; }} />
        </label>
        <label>Role
          <select @change=${(event: Event) => { this.newRole = (event.target as HTMLSelectElement).value; }}>
            <option value="user" ?selected=${this.newRole === 'user'}>User</option>
            <option value="admin" ?selected=${this.newRole === 'admin'}>Admin</option>
          </select>
        </label>
        <button class="primary" type="submit">Add person</button>
        </div>
      </form>
      <div class="roster">
        ${this.users.length ? this.users.map((user) => {
          const draft = this.draft(user);
          return html`
            <div class="person">
              <div class="person-head">
                <div class="email">${user.email}</div>
                <span class="role">${draft.role === 'admin' ? 'Admin' : 'User'}</span>
              </div>
              <div class="fields">
              <label>Mobile
                <input .value=${draft.phone} @input=${(event: Event) => this.setDraft(user.id, { phone: (event.target as HTMLInputElement).value })} />
              </label>
              <label>Role
                <select @change=${(event: Event) => this.setDraft(user.id, { role: (event.target as HTMLSelectElement).value })}>
                  <option value="user" ?selected=${draft.role === 'user'}>User</option>
                  <option value="admin" ?selected=${draft.role === 'admin'}>Admin</option>
                </select>
              </label>
              <label class="check">
                <input type="checkbox" .checked=${draft.active} @change=${(event: Event) => this.setDraft(user.id, { active: (event.target as HTMLInputElement).checked })} />
                Active
              </label>
              <label>New password
                <input type="text" placeholder="Leave blank to keep" .value=${draft.password}
                  @input=${(event: Event) => this.setDraft(user.id, { password: (event.target as HTMLInputElement).value })} />
              </label>
              <button type="button" class="primary" @click=${() => this.save(user)}>Save</button>
              </div>
            </div>
          `;
        }) : html`<p class="empty">No one else is on the list yet.</p>`}
      </div>
    `;
  }
}

async function readError(res: Response) {
  try {
    const body = await res.json();
    return typeof body.detail === 'string' ? body.detail : 'Could not save that person.';
  } catch {
    return 'Could not save that person.';
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'fp3-users': Fp3Users;
  }
}
